import { Injectable, signal } from '@angular/core';
import { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';

/** Ephemeral owner selections; the workspace provides one instance per draft session. */
@Injectable()
export class WorkflowOwnerViewState {
  private readonly entries = new Map<string, unknown>();
  private readonly focusRequests = new Map<string, WeakSet<object>>();
  private generation = 0;
  private focusSuspensions = 0;
  private readonly focusSuspendedState = signal(false);
  readonly focusSuspended = this.focusSuspendedState.asReadonly();

  /** Background editor focus yields while the workspace's confirmation owns focus. */
  suspendFocus(): () => void {
    this.focusSuspensions++;
    this.focusSuspendedState.set(true);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.focusSuspensions--;
      this.focusSuspendedState.set(this.focusSuspensions > 0);
    };
  }

  key(document: WorkflowDefinitionDocument, nodeId: string, owner: string): string {
    return JSON.stringify([
      this.generation,
      document.workflowCode,
      document.definitionVersionCode,
      document.packReference,
      nodeId,
      owner,
    ]);
  }

  remember<T>(key: string, state: T): void {
    this.entries.set(key, structuredClone(state));
  }

  read<T>(key: string): T | undefined {
    const state = this.entries.get(key);
    return state === undefined ? undefined : structuredClone(state) as T;
  }

  hasConsumedFocus(key: string, request: object): boolean {
    return this.focusRequests.get(key)?.has(request) === true;
  }

  consumeFocus(key: string, request: object): void {
    if (!this.focusRequests.has(key)) this.focusRequests.set(key, new WeakSet<object>());
    this.focusRequests.get(key)!.add(request);
  }

  /** Called only when the workspace changes draft identity or closes it. */
  clear(): void {
    this.generation++;
    this.entries.clear();
    this.focusRequests.clear();
  }
}

/** A uniquely labelled control, never a document position or a retained DOM node. */
export interface WorkflowOwnerFocus {
  tag: string;
  attribute: 'data-owner-focus' | 'aria-label' | 'name' | 'id' | 'label';
  value: string;
}

export function captureWorkflowOwnerFocus(host: HTMLElement, target: EventTarget | null): WorkflowOwnerFocus | null {
  if (!(target instanceof HTMLElement) || !host.contains(target)) return null;
  for (const attribute of ['data-owner-focus', 'aria-label', 'name', 'id'] as const) {
    const value = target.getAttribute(attribute);
    if (value && Array.from(host.querySelectorAll<HTMLElement>(target.tagName))
      .filter(element => element.getAttribute(attribute) === value).length === 1)
      return { tag: target.tagName, attribute, value };
  }
  const value = controlLabel(target);
  if (value && Array.from(host.querySelectorAll<HTMLElement>(target.tagName)).filter(element => controlLabel(element) === value).length === 1)
    return { tag: target.tagName, attribute: 'label', value };
  return null;
}

function controlLabel(element: HTMLElement): string {
  if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) return '';
  return Array.from(element.labels ?? []).map(label => label.textContent?.replace(/\s+/g, ' ').trim() ?? '').join(' ');
}

/** Returns false when the exact control disappeared, duplicated, or is hidden. */
export function restoreWorkflowOwnerFocus(host: HTMLElement, focus: WorkflowOwnerFocus | null): boolean {
  if (!focus || !host.isConnected || host.closest('[hidden], [inert]')) return false;
  const matches = Array.from(host.querySelectorAll<HTMLElement>(focus.tag))
    .filter(element => (focus.attribute === 'label' ? controlLabel(element) : element.getAttribute(focus.attribute)) === focus.value &&
      !element.closest('[hidden], [inert]') &&
      !(element instanceof HTMLInputElement && element.disabled) &&
      !(element instanceof HTMLButtonElement && element.disabled) &&
      !(element instanceof HTMLTextAreaElement && element.disabled) &&
      !(element instanceof HTMLSelectElement && element.disabled));
  if (matches.length !== 1) return false;
  matches[0].focus({ preventScroll: true });
  return host.ownerDocument.activeElement === matches[0];
}
